import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type React from 'react';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { Input } from '@/shared/components/ui/input';

import type { PasswordInputProps, PasswordInputRef } from './PasswordInput.types';

const PasswordInput = forwardRef<PasswordInputRef, PasswordInputProps>(
  (
    {
      customInput,
      inputFormStyle,
      placeholder,
      value = '',
      id,
      required = false,
      name,
      maxLength,
      onChange = () => { },
      onKeyUp = () => { },
      state = 'default',
      pattern = '',
      disabled,
      styleIcon,
      ..._props
    },
    ref
  ) => {
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const [inputValue, setInputValue] = useState(value);

    useEffect(() => {
      setInputValue(value);
    }, [value]);

    const togglePasswordVisibility = () => {
      setIsPasswordVisible((prev) => !prev);
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;

      if (pattern) {
        const regex = new RegExp(pattern);
        if (!regex.test(newValue)) return;
      }

      setInputValue(newValue);
      onChange(event);
    };

    useImperativeHandle(ref, () => ({
      focus: () => inputRef.current?.focus(),
      empty: () => setInputValue(''),
    }));

    return (
      <div className={cn('relative', customInput)}>
        <Input
          ref={inputRef}
          type={isPasswordVisible ? 'text' : 'password'}
          name={name}
          placeholder={placeholder}
          className={cn('input pr-10', inputFormStyle)}
          value={inputValue}
          id={id}
          maxLength={maxLength}
          disabled={disabled}
          onKeyUp={onKeyUp}
          onChange={handleInputChange}
        />

        {!disabled && (
          <FontAwesomeIcon
            icon={isPasswordVisible ? faEye : faEyeSlash}
            onClick={togglePasswordVisibility}
            style={{ cursor: 'pointer' }}
            className={cn(
              'absolute top-1/2 right-3 transform -translate-y-1/2 hover:opacity-90 w-[30px]',
              'text-neutral-600 dark:text-text-white',
              styleIcon
            )}
          />
        )}
      </div>
    );
  }
);

export default PasswordInput;
